import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { BarChart3, Loader2, RefreshCw, TrendingUp, Users, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const EpidemiologiaPage = () => {
  const { api, user } = useAuth();
  const [indicadores, setIndicadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  useEffect(() => {
    fetchIndicadores();
  }, []);

  const fetchIndicadores = async () => {
    try {
      const response = await api.get('/epidemiologia');
      setIndicadores(response.data);
    } catch (error) {
      console.error('Error fetching indicadores:', error);
      toast.error('Erro ao carregar indicadores');
    } finally {
      setLoading(false);
    }
  };

  const analisar = async () => {
    setAnalyzing(true);
    const now = new Date();
    const periodo = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    try {
      await api.post(`/epidemiologia/analisar?empresa_id=${user?.empresa_id}&periodo=${periodo}`);
      toast.success('Análise concluída!');
      fetchIndicadores();
    } catch (error) {
      toast.error('Erro ao analisar');
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const latestIndicador = indicadores[0];
  
  const cidsData = latestIndicador?.cids_frequentes 
    ? Object.entries(latestIndicador.cids_frequentes).map(([name, value]) => ({ name, value }))
    : [];

  const setoresData = latestIndicador?.setores_afetados
    ? Object.entries(latestIndicador.setores_afetados).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="epidemiologia-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Inteligência Epidemiológica</h2>
          <p className="text-muted-foreground">Análise de afastamentos e indicadores de saúde</p>
        </div>
        <Button 
          onClick={analisar}
          disabled={analyzing}
          className="bg-accent hover:bg-accent/90"
          data-testid="analisar-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${analyzing ? 'animate-spin' : ''}`} />
          {analyzing ? 'Analisando...' : 'Atualizar Análise'}
        </Button>
      </div>

      {latestIndicador ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Afastamentos</p>
                    <p className="text-2xl font-bold">{latestIndicador.total_afastamentos}</p>
                  </div>
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                    <Stethoscope className="w-6 h-6 text-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Média Dias</p>
                    <p className="text-2xl font-bold">{latestIndicador.media_dias_afastamento}</p>
                  </div>
                  <div className="w-12 h-12 bg-info/10 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-info" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">CIDs Distintos</p>
                    <p className="text-2xl font-bold">{Object.keys(latestIndicador.cids_frequentes || {}).length}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Setores Afetados</p>
                    <p className="text-2xl font-bold">{Object.keys(latestIndicador.setores_afetados || {}).length}</p>
                  </div>
                  <div className="w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-warning" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts */}
          {latestIndicador.alertas?.length > 0 && (
            <Card className="border-warning/50 bg-warning/5">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit'] text-warning">Alertas Epidemiológicos</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {latestIndicador.alertas.map((alerta, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 bg-warning rounded-full" />
                      {alerta}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">CIDs Mais Frequentes</CardTitle>
              </CardHeader>
              <CardContent>
                {cidsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={cidsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px'
                        }} 
                      />
                      <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    Sem dados de CID
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg font-['Outfit']">Afastamentos por Setor</CardTitle>
              </CardHeader>
              <CardContent>
                {setoresData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={setoresData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {setoresData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    Sem dados de setor
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card className="border-slate-100">
          <CardContent className="pt-12 pb-12">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhuma análise epidemiológica disponível</p>
              <p className="text-sm text-muted-foreground mt-1">
                Clique em "Atualizar Análise" para gerar os indicadores
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EpidemiologiaPage;
